import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDarkMode } from '../../context/DarkModeContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { SaveIcon, UploadIcon, PlusIcon, XIcon } from 'lucide-react';
import BackButton from '../../components/UI/BackButton';
const BookEdit = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const isNew = !id;
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    description: '',
    genre: '',
    subgenre: '',
    price: '',
    isbn: '',
    pageCount: '',
    publishDate: '',
    tags: [] as string[],
    contentWarnings: [] as string[]
  });
  const [tagInput, setTagInput] = useState('');
  const handleAddTag = () => {
    if (tagInput.trim()) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };
  const handleRemoveTag = (index: number) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };
  const inputClass = `w-full rounded-md px-3 py-2 ${dk.inputOnCard} ${rt.inputFocus}`;
  const labelClass = `block text-sm font-medium mb-1 ${dk.textSecondary}`;
  return <div className={`min-h-screen py-8 ${dk.page} ${dk.textPrimary}`} data-id="element-17853" data-mp="1q0k5ox745ps4">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-17854" data-mp="1l0o2v1a5k4e5">
        <div className="flex items-center justify-between mb-8" data-id="element-17855" data-mp="1l28sqdliuz5o">
          <div className="flex items-center" data-id="element-17856" data-mp="1zqdexrjs9st7">
            <div className="mr-4" data-id="element-17857" data-mp="1zr534hb1wo9g">
              <BackButton data-id="element-17858" data-mp="1uybwfnw0v5uq" />
            </div>
            <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-17859" data-mp="1uxaldjf0ajd0">
              {isNew ? 'Add New Book' : 'Edit Book Details'}
            </h1>
          </div>
          <div className="flex space-x-3" data-id="element-17860" data-mp="12apqwa38hr58">
            <button className={`px-4 py-2 border rounded-md ${dk.card}`} data-id="element-17861" data-mp="127tuiq71baiw">
              Save Draft
            </button>
            <button className={`flex items-center px-4 py-2 rounded-md text-white ${rt.primaryButton}`} data-id="element-17862" data-mp="1vzni07hpvoig">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-17863" data-mp="20q3zq3sklwam" />
              Publish Changes
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-17864" data-mp="25ooydys6c7wy">
          {/* Left Column - Cover Image */}
          <div className="lg:col-span-1" data-id="element-17865" data-mp="191ajw762yl1k">
            <div className={`p-6 rounded-xl shadow-sm border ${dk.card}`} data-id="element-17866" data-mp="1d09mvm8947qd">
              <h3 className={`font-medium mb-4 ${dk.textHeading}`} data-id="element-17867" data-mp="1hzvyxsccfhxl">
                Book Cover
              </h3>
              <div className={`aspect-[2/3] rounded-lg border-2 border-dashed flex flex-col items-center justify-center ${dk.cardElevated} ${dk.border}`} data-id="element-17868" data-mp="1mypfxuqu9r2i">
                <UploadIcon className={`h-12 w-12 mb-2 ${dk.textMuted}`} data-id="element-17869" data-mp="2ye5lqi0xsymb" />
                <p className={`text-sm ${dk.textSecondary}`} data-id="element-17870" data-mp="2yg8nfzbgcg6v">
                  Click to upload cover
                </p>
                <p className={`text-xs mt-1 ${dk.textMuted}`} data-id="element-17871" data-mp="hrpiks52t8v3">
                  JPG, PNG up to 5MB
                </p>
              </div>
              <button className={`mt-4 w-full py-2 border rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 ${dk.border} ${dk.textPrimary}`} data-id="element-17872" data-mp="czxqgd69uv2u">
                Change Cover
              </button>
            </div>
          </div>

          {/* Right Column - Form Fields */}
          <div className="lg:col-span-2 space-y-6" data-id="element-17873" data-mp="3v5126gqqu5c8">
            {/* Basic Info */}
            <div className={`p-6 rounded-xl shadow-sm border ${dk.card}`} data-id="element-17874" data-mp="3q8ajrmrguxnn">
              <h3 className={`font-medium mb-4 text-lg ${dk.textHeading}`} data-id="element-17875" data-mp="3l97q0ygxsduz">
                Basic Information
              </h3>
              <div className="grid grid-cols-1 gap-6" data-id="element-17876" data-mp="1ofmdf4jizu3n">
                <div data-id="element-17877" data-mp="1jhb5izcsjto2">
                  <label className={labelClass} data-id="element-17878" data-mp="1ji4em6z82v9d">Book Title</label>
                  <input type="text" className={inputClass} placeholder="Enter book title" data-id="element-17879" data-mp="1rhr3zxj3l0k2" />
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-17880" data-mp="1hjl6rt88kilj">
                  <div data-id="element-17881" data-mp="1hj1na82xtm5e">
                    <label className={labelClass} data-id="element-17882" data-mp="1hlnta3a6jtli">Genre</label>
                    <select className={inputClass} data-id="element-17883" data-mp="1mhcx4it8fwnm">
                      <option data-id="element-17884" data-mp="18r9ioe0lh78q">Select Genre</option>
                      <option data-id="element-17885" data-mp="18qhmp23357hx">Romance</option>
                      <option data-id="element-17886" data-mp="1dhzkjh9taryo">Fantasy</option>
                      <option data-id="element-17887" data-mp="1dirigyqvt1s4">Thriller</option>
                    </select>
                  </div>
                  <div data-id="element-17888" data-mp="yu4vqbyxd7qv">
                    <label className={labelClass} data-id="element-17889" data-mp="yuwn1alhojbc">Subgenre</label>
                    <input type="text" className={inputClass} placeholder="e.g. Dark Romance" data-id="element-17890" data-mp="13t7zlt3657x9" />
                  </div>
                </div>
                <div data-id="element-17891" data-mp="ttgznbvua0ps">
                  <label className={labelClass} data-id="element-17892" data-mp="tvtjjul5ql5h">Description</label>
                  <textarea rows={6} className={inputClass} placeholder="Book description..." data-id="element-17893" data-mp="b688un3p3e56" />
                </div>
              </div>
            </div>

            {/* Pricing & Details */}
            <div className={`p-6 rounded-xl shadow-sm border ${dk.card}`} data-id="element-17894" data-mp="f70ihhghbl1z">
              <h3 className={`font-medium mb-4 text-lg ${dk.textHeading}`} data-id="element-17895" data-mp="u4kg23b1qdy4">
                Pricing & Details
              </h3>
              <div className="grid grid-cols-2 gap-6" data-id="element-17896" data-mp="p6967skr43fw">
                <div data-id="element-17897" data-mp="p3n0lyhf7y0b">
                  <label className={labelClass} data-id="element-17898" data-mp="12xe1kih369kf">Price ($)</label>
                  <input type="number" step="0.01" className={inputClass} placeholder="0.00" data-id="element-17899" data-mp="12yyu62jubifn" />
                </div>
                <div data-id="element-17900" data-mp="17vz3cmbzl1az">
                  <label className={labelClass} data-id="element-17901" data-mp="17wh7040zahsx">Page Count</label>
                  <input type="number" className={inputClass} placeholder="e.g. 350" data-id="element-17902" data-mp="17u4fp09byv37" />
                </div>
                <div data-id="element-17903" data-mp="9kefpa4vcaiv">
                  <label className={labelClass} data-id="element-17904" data-mp="ej7quvwonpfv">ISBN (Optional)</label>
                  <input type="text" className={inputClass} placeholder="ISBN-13" data-id="element-17905" data-mp="ehwp11mssrre" />
                </div>
                <div data-id="element-17906" data-mp="o898eqivmevy">
                  <label className={labelClass} data-id="element-17907" data-mp="ob556bizmxit">Publish Date</label>
                  <input type="date" className={inputClass} data-id="element-17908" data-mp="t6sseldwtglo" />
                </div>
              </div>
            </div>

            {/* Tags & Metadata */}
            <div className={`p-6 rounded-xl shadow-sm border ${dk.card}`} data-id="element-17909" data-mp="128trzowm8eqi">
              <h3 className={`font-medium mb-4 text-lg ${dk.textHeading}`} data-id="element-17910" data-mp="126plcz7c11mj">
                Tags & Tropes
              </h3>
              <div className="mb-4" data-id="element-17911" data-mp="33zx0a7j19h7w">
                <div className="flex gap-2 mb-2" data-id="element-17912" data-mp="340ydahjm0ds9">
                  <input type="text" value={tagInput} onChange={e => setTagInput(e.target.value)} className={`flex-1 ${inputClass}`} placeholder="Add a tag or trope..." onKeyDown={e => e.key === 'Enter' && handleAddTag()} data-id="element-17913" data-mp="2wgnq4c2pdehs" />

                  <button onClick={handleAddTag} className={`px-4 py-2 rounded-md ${dk.cardElevated}`} data-id="element-17914" data-mp="2htku69u13dwk">
                    <PlusIcon className={`h-5 w-5 ${dk.textPrimary}`} data-id="element-17915" data-mp="3ag5lxbef1s0n" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-17916" data-mp="35g10d6yni73k">
                  {formData.tags.map((tag, index) => <span key={index} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${rt.bgMedium} ${rt.textPrimary}`} data-id="element-17917" data-mp="31fh6cc9djjln" data-mpi={index}>
                      {tag}
                      <button onClick={() => handleRemoveTag(index)} className={`ml-2 hover:opacity-75`} data-id="element-17918" data-mp="3p5btaaa00arj">
                        <XIcon className="h-3 w-3" data-id="element-17919" data-mp="3u2bzcl6uldin" />
                      </button>
                    </span>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default BookEdit;